import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Users, Plane } from 'lucide-react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { supabase } from '../lib/supabase';
import { getLocalizedPath } from '../lib/i18n';
import { Language } from '../translations';

interface RegistrationProps {
    t: any;
    lang: Language;
    expeditions?: any[];
}

const Registration: React.FC<RegistrationProps> = ({ t, lang, expeditions = [] }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const [searchParams] = useSearchParams();
    const queryBookingId = searchParams.get('booking_id') || searchParams.get('id');
    const queryStep = searchParams.get('step') || searchParams.get('pay');
    const [bookingPaymentStatus, setBookingPaymentStatus] = useState<string>('');
    const [dbTotalPrice, setDbTotalPrice] = useState<number | null>(null);

    const [expId, setExpId] = useState<string>(location.state?.expId || '');
    const [variantId, setVariantId] = useState<string>(location.state?.variantId || '');
    const [date, setDate] = useState<string>(location.state?.date || '');
    const [airport, setAirport] = useState<boolean>(location.state?.airport || false);
    const [leadName, setLeadName] = useState<string>(location.state?.leadName || '');
    const [leadEmailFromState, setLeadEmailFromState] = useState<string>(location.state?.leadEmail || '');
    const [selectedServices, setSelectedServices] = useState<string[]>(location.state?.selectedServices || []);
    const [selectedUpgrades, setSelectedUpgrades] = useState<string[]>(location.state?.selectedUpgrades || []);
    const passedTotal = location.state?.total;
    const [people, setPeople] = useState<number>(location.state?.people || 1);

    const [loading, setLoading] = useState(false);
    const [showPayPal, setShowPayPal] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'pix'>('pix');
    const [virtualPosLoading, setVirtualPosLoading] = useState(false);
    const [virtualPosError, setVirtualPosError] = useState<string | null>(null);
    const [participantsData, setParticipantsData] = useState<any[]>([]);
    const [bookingId, setBookingId] = useState<string | null>(null);

    const [waiverAccepted, setWaiverAccepted] = useState(false);
    const [policyAccepted, setPolicyAccepted] = useState(false);
    const [bookingLoaded, setBookingLoaded] = useState(false);

    useEffect(() => {
        if (queryBookingId) {
            setBookingId(queryBookingId);
            const loadBooking = async () => {
                setLoading(true);
                try {
                    const { data, error } = await supabase
                        .from('bookings')
                        .select('*')
                        .eq('id', queryBookingId)
                        .single();
                    if (error) throw error;
                    if (data) {
                        setExpId(data.expedition_slug);
                        setDate(data.start_date);
                        setPeople(data.people_count);
                        setAirport(data.airport_transfer);
                        setLeadName(data.contact_name);
                        setLeadEmailFromState(data.contact_email);
                        
                        // Prefill participants data from DB if exists
                        if (data.participants && Array.isArray(data.participants)) {
                            setParticipantsData(data.participants);
                        }
                        
                        setBookingLoaded(true);
                    }
                } catch (err) {
                    console.error("Error loading booking details:", err);
                } finally {
                    setLoading(false);
                }
            };
            loadBooking();
        }
    }, [queryBookingId]);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Fallback constraint if someone visits the URL directly
        if (!expId && !queryBookingId && process.env.NODE_ENV !== 'development') {
            navigate(getLocalizedPath('/expeditions', lang));
        }
    }, [expId, queryBookingId, navigate]);

    // Handle ESC key to safely exit the payment modal with confirmation
    useEffect(() => {
        if (!showPayPal) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showExitConfirm) {
                    setShowExitConfirm(false);
                } else {
                    setShowExitConfirm(true);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showPayPal, showExitConfirm]);

    const isEn = lang === 'en';

    // Pricing Logic re-computation
    const exp = expeditions.find(e => e.id === expId);
    
    const prices = useMemo(() => {
        if (!exp) {
            if (passedTotal) return { perPerson: Math.round(passedTotal / people), total: passedTotal };
            return null;
        }

        let perPerson = 0;
        let total = 0;

        // 1. Flat price support
        const flatPrice = typeof exp.price_flat === 'number' ? exp.price_flat : parseFloat(String(exp.price_flat || ''));
        if (flatPrice && !isNaN(flatPrice) && flatPrice > 0) {
            perPerson = flatPrice;
            total = perPerson * people;
        } else {
            // 2. Dynamic tiered pricing
            let pricingTiers = (exp.pricing_tiers && exp.pricing_tiers.length > 0) ? exp.pricing_tiers : null;
            if (exp.variants && variantId) {
                const variant = exp.variants.find((v: any) => v.id === variantId);
                if (variant && variant.pricing_tiers && variant.pricing_tiers.length > 0) {
                    pricingTiers = variant.pricing_tiers;
                }
            }

            if (pricingTiers && pricingTiers.length > 0) {
                const sortedTiers = [...pricingTiers].sort((a: any, b: any) => b.min_people - a.min_people);
                let applicableTier = sortedTiers.find((tier: any) => people >= tier.min_people);
                if (!applicableTier && sortedTiers.length > 0) {
                    applicableTier = sortedTiers[sortedTiers.length - 1];
                }
                if (applicableTier) {
                    perPerson = applicableTier.price_per_person;
                    total = perPerson * people;
                }
            }

            // 3. Fallbacks if no tier matched
            if (!perPerson) {
                if (exp.id === 'laguna') {
                    if (people === 1) perPerson = 170;
                    else if (people < 4) perPerson = 90;
                    else perPerson = 70;
                    total = perPerson * people;
                } else if (exp.id === 'horquetas') {
                    if (people === 1) perPerson = 900;
                    else if (people === 2) perPerson = 700;
                    else perPerson = 500;
                    total = perPerson * people;
                } else if (exp.price_from && Number(exp.price_from) > 0) {
                    perPerson = Number(exp.price_from);
                    total = perPerson * people;
                } else if (passedTotal) {
                    return { perPerson: Math.round(passedTotal / people), total: passedTotal };
                } else {
                    return null;
                }
            }
        }

        if (airport) total += 50;

        // Upgrades
        let upgradesCost = 0;
        if (exp.price_upgrades && exp.price_upgrades.length > 0) {
            exp.price_upgrades.forEach((upg: any) => {
                if (selectedUpgrades.includes(upg.name_es)) {
                    upgradesCost += upg.price * people;
                }
            });
        }
        total += upgradesCost;

        return { perPerson, total };
    }, [exp, people, airport, variantId, selectedUpgrades, passedTotal]);

    const displayDate = date 
        ? new Date(date).toLocaleDateString(isEn ? 'en-US' : 'es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) 
        : (isEn ? 'Date not specified' : 'Fecha no especificada');
    // 50% Deposit & 48h Balance Payment Logic
    const isBalancePayment = queryStep === 'balance' || queryStep === 'final_payment' || bookingPaymentStatus === 'partially_paid';
    const totalPrice = prices?.total || dbTotalPrice || 0;
    const depositAmount = Math.round(totalPrice * 0.5);
    const balanceAmount = totalPrice - depositAmount;
    const chargeAmount = isBalancePayment ? balanceAmount : depositAmount;
    const triggerStatusEmail = async (status: string, bId: string, paypalId: string = '', currentParticipants: any[] = participantsData) => {
        const leadEmail = leadEmailFromState;
        const customerName = leadName || "Explorador";
        
        // Inject email and selected_services into lead participant
        const updatedParticipants = [...currentParticipants];
        if (updatedParticipants.length > 0) {
            updatedParticipants[0] = {
                ...updatedParticipants[0],
                email: leadEmailFromState
            };
            if (selectedServices) {
                updatedParticipants[0].selected_services = selectedServices;
            }
        }

        try {
            const response = await fetch(
                'https://iuixlsqrlfkfnvnckgij.supabase.co/functions/v1/booking-status-email',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        new_status: status,
                        booking_id: bId,
                        customer_name: customerName,
                        customer_email: leadEmail,
                        booking_details: {
                            expedition_slug: expId || 'unknown',
                            expedition_name: exp ? (isEn ? exp.name_en : exp.name_es) : 'Expedición de Montaña',
                            start_date: date,
                            people_count: people,
                            total_price: prices?.total || 0,
                            airport_transfer: airport,
                            paypal_order_id: paypalId,
                            participants: updatedParticipants
                        }
                    })
                }
            );
            const result = await response.json();
            if (result.success) {
                console.log(`✅ Email sent via Edge Function. Template: ${result.template}, Resend ID: ${result.resend_id}`);
            } else {
                console.error('Edge Function error sending status email:', result);
            }
        } catch (err) {
            console.error(`Error triggering booking-status-email Edge Function for ${status}:`, err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);
        const parts: any[] = [];
        
        for (let i = 0; i < people; i++) {
            parts.push({
                fullName: formData.get(`fullName_${i}`),
                passport: formData.get(`passport_${i}`),
                dob: formData.get(`dob_${i}`),
                shoeSize: formData.get(`shoeSize_${i}`),
                clothingSize: formData.get(`clothingSize_${i}`),
                diet: formData.get(`diet_${i}`),
                health: formData.get(`health_${i}`),
                emergencyName: formData.get(`emergencyName_${i}`),
                emergencyPhone: formData.get(`emergencyPhone_${i}`)
            });
        }
        
        setParticipantsData(parts);

        const leadEmail = leadEmailFromState;
        const updatedParticipants = [...parts];
        if (updatedParticipants.length > 0) {
            updatedParticipants[0] = {
                ...updatedParticipants[0],
                email: leadEmailFromState
            };
            if (selectedServices) {
                updatedParticipants[0].selected_services = selectedServices;
            }
        }

        if (!waiverAccepted || !policyAccepted) {
            alert(isEn ? 'You must accept the terms and policies to proceed.' : 'Debes aceptar los términos y políticas para continuar.');
            setLoading(false);
            return;
        }

        let currentBookingId = bookingId;

        try {
            if (bookingId) {
                // Update existing booking details and participant info
                const { error } = await supabase
                    .from('bookings')
                    .update({
                        people_count: people,
                        total_price: prices?.total || 0,
                        airport_transfer: airport,
                        participants: updatedParticipants,
                        contact_name: updatedParticipants[0]?.fullName || leadName,
                        contact_email: leadEmailFromState
                    })
                    .eq('id', bookingId);

                if (error) throw error;
                console.log("Existing booking updated with participant details:", bookingId);
            } else {
                // Insert a new pending booking
                const pendingBookingData = {
                    expedition_slug: expId || 'unknown',
                    start_date: date,
                    people_count: people,
                    total_price: prices?.total || 0,
                    airport_transfer: airport,
                    payment_status: 'pending',
                    paypal_order_id: '',
                    participants: updatedParticipants,
                    contact_name: updatedParticipants[0]?.fullName || leadName,
                    contact_email: leadEmailFromState
                };

                const { data, error } = await supabase
                    .from('bookings')
                    .insert([pendingBookingData])
                    .select('id')
                    .single();

                if (error) throw error;
                
                if (data) {
                    currentBookingId = data.id;
                    setBookingId(data.id);
                    console.log("Pending booking saved to Supabase with ID:", data.id);
                    // Trigger booking-status-email Edge Function for pending status
                    await triggerStatusEmail('pending', data.id, '', updatedParticipants);
                }
            }
        } catch (err) {
            console.error("Error creating/updating pending booking:", err);
        } finally {
            setLoading(false);
            setShowPayPal(true);
        }
    };

    const handleStartVirtualPosPayment = async () => {
        setVirtualPosLoading(true);
        setVirtualPosError(null);

        try {
            const customerLead = participantsData[0] || {};
            const customerName = customerLead.fullName || leadName || "Explorador";
            const customerEmail = customerLead.email || leadEmailFromState;
            const customerPhone = customerLead.emergencyPhone || "56999999999";
            const expName = exp ? (isEn ? exp.name_en : exp.name_es) : 'Expedición de Montaña';

            // Call Supabase Edge Function
            const res = await fetch(
                'https://iuixlsqrlfkfnvnckgij.supabase.co/functions/v1/super-processor',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        booking_id: bookingId,
                        amount: chargeAmount,
                        currency: 'USD',
                        customer_name: customerName,
                        customer_email: customerEmail,
                        customer_phone: customerPhone,
                        social_id: customerLead.passport || '11111111-1',
                        expedition_name: isBalancePayment ? `${expName} (Saldo 50%)` : `${expName} (Abono 50%)`,
                        origin_url: window.location.origin
                    })
                }
            );

            const result = await res.json();

            if (result.success && result.url_redirect) {
                // Update booking with payment UUID before redirecting
                if (bookingId && result.payment_uuid) {
                    await supabase
                        .from('bookings')
                        .update({
                            paypal_order_id: `VIRTUALPOS-${result.payment_uuid}`
                        })
                        .eq('id', bookingId);
                }
                // Redirect user to VirtualPOS Checkout (where PIX is available)
                window.location.href = result.url_redirect;
            } else {
                console.error("Error from create-virtualpos-payment:", result);
                setVirtualPosError(
                    result.error || (isEn ? 'Could not generate VirtualPOS checkout. Please try PayPal.' : 'No se pudo generar la sesión de pago de VirtualPOS. Intenta con PayPal.')
                );
            }
        } catch (err: any) {
            console.error("Exception in handleStartVirtualPosPayment:", err);
            setVirtualPosError(
                err.message || (isEn ? 'Connection error. Please try again.' : 'Error de conexión. Por favor reintenta.')
            );
        } finally {
            setVirtualPosLoading(false);
        }
    };


    return (
        <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 bg-[#0a0a0a]">
            <div className="max-w-6xl mx-auto">
                <div className="text-center space-y-4 mb-12">
                    <h1 className="text-4xl md:text-5xl font-title uppercase tracking-tighter text-[#00d4ff]">
                        {isEn ? 'Step 2: Participant Registration' : 'Paso 2: Registro de Participantes'}
                    </h1>
                    <p className="text-gray-400 font-light italic text-sm md:text-lg max-w-2xl mx-auto">
                        {isEn ? 'Review your expedition details and complete the vital information for each participant to finalize your spot.' : 'Revisa los detalles de tu expedición y completa la información vital de cada participante para finalizar la reserva.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-8 items-start relative">
                    <div className="flex-1 space-y-8 w-full">
                    {Array.from({ length: people }).map((_, i) => (
                        <div key={i} className="bg-[#1a1512] border border-rock p-6 md:p-10 shadow-xl relative overflow-hidden group">
                            {/* Decorative number */}
                            <div className="absolute top-0 right-0 p-8 text-8xl font-title text-white/5 select-none transition-transform group-hover:scale-110 duration-500 pointer-events-none">
                                0{i + 1}
                            </div>
                            
                            <h2 className="text-xl font-title uppercase tracking-widest text-white mb-8 border-b border-rock pb-4 relative z-10 flex items-center gap-3">
                                <span className="w-2 h-2 bg-[#00d4ff] block"></span>
                                {isEn ? 'Participant' : 'Participante'} {i + 1}
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                                <div>
                                    <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block">
                                        {isEn ? 'Full Name' : 'Nombre Completo'} <span className="text-[#00d4ff]">*</span>
                                    </label>
                                    <input name={`fullName_${i}`} type="text" required defaultValue={participantsData[i]?.fullName || (i === 0 ? leadName : '')} placeholder={isEn ? 'John Doe' : 'Ej: Juan Pérez'} className="w-full h-12 bg-black border border-rock px-4 text-xs text-white outline-none focus:border-[#00d4ff] transition-colors" />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block">
                                        {isEn ? 'ID / Passport' : 'RUT / DNI / Pasaporte'} <span className="text-[#00d4ff]">*</span>
                                    </label>
                                    <input name={`passport_${i}`} type="text" required defaultValue={participantsData[i]?.passport || ''} className="w-full h-12 bg-black border border-rock px-4 text-xs text-white outline-none focus:border-[#00d4ff] transition-colors" />
                                </div>
                                <div>
                                    <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block">
                                        {isEn ? 'Date of Birth' : 'Fecha de Nacimiento'} <span className="text-[#00d4ff]">*</span>
                                    </label>
                                    <input name={`dob_${i}`} type="date" required defaultValue={participantsData[i]?.dob || ''} className="w-full h-12 bg-black border border-rock px-3 text-xs text-gray-300 outline-none focus:border-[#00d4ff] transition-colors appearance-none" />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block truncate">
                                            {isEn ? 'Shoe Size' : 'Talla de Calzado'} <span className="text-[#00d4ff]">*</span>
                                        </label>
                                        <input name={`shoeSize_${i}`} type="text" required defaultValue={participantsData[i]?.shoeSize || ''} placeholder={isEn ? 'Ej: US 10' : 'Ej: 42 (EUR)'} className="w-full h-12 bg-black border border-rock px-4 text-xs text-white outline-none focus:border-[#00d4ff] transition-colors" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block truncate">
                                            {isEn ? 'Clothing Size' : 'Talla Ropa'} <span className="text-[#00d4ff]">*</span>
                                        </label>
                                        <select name={`clothingSize_${i}`} required defaultValue={participantsData[i]?.clothingSize || ''} className="w-full h-12 bg-black border border-rock px-4 text-xs text-white outline-none focus:border-[#00d4ff] transition-colors appearance-none cursor-pointer">
                                            <option value="" disabled hidden>{isEn ? 'Select...' : 'Seleccionar...'}</option>
                                            <option value="S">S</option>
                                            <option value="M">M</option>
                                            <option value="L">L</option>
                                            <option value="XL">XL</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-span-1 md:col-span-2 -mt-4 text-[9px] text-[#00d4ff]/70 block mb-2 italic px-1">
                                    * {isEn ? 'Vital for hard shell boots and technical climbing harness sizing.' : 'Para el alquiler de las botas rígidas y ajuste correcto del arnés.'}
                                </div>
                                
                                <div className="md:col-span-2">
                                    <label className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-2 block">
                                        {isEn ? 'Dietary Restrictions' : 'Restricciones Alimentarias'}
                                    </label>
                                    <input name={`diet_${i}`} type="text" defaultValue={participantsData[i]?.diet || ''} placeholder={isEn ? 'Vegan, celiac, etc.' : 'Vegano, celiaco, etc.'} className="w-full h-12 bg-black border border-rock px-4 text-xs text-white outline-none focus:border-[#00d4ff] transition-colors" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-[10px] text-white uppercase tracking-widest font-bold mb-2 block">
                                        {isEn ? 'Medical conditions, allergies or injuries' : 'Enfermedades, alergias o lesiones previas'} <span className="text-red-500 tracking-normal">*</span>
                                    </label>
                                    <textarea name={`health_${i}`} required defaultValue={participantsData[i]?.health || ''} rows={3} placeholder={isEn ? 'Describe any relevant medical condition...' : 'Describa cualquier condición médica, limitación física antigua o alergia a medicamentos...'} className="w-full bg-black border border-rock p-4 text-xs text-white outline-none focus:border-red-500/50 transition-colors resize-none"></textarea>
                                </div>
                                <div className="md:col-span-2 border-t border-rock pt-5 mt-2">
                                    <label className="text-[10px] text-[#00d4ff] uppercase tracking-widest font-bold mb-3 block">
                                        {isEn ? 'Emergency Contact' : 'Contacto de Emergencia'} <span className="text-[#00d4ff]">*</span>
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[9px] text-[#00d4ff]/60 uppercase tracking-widest font-bold mb-2 block">
                                                {isEn ? 'Full Name & Relationship' : 'Nombre Completo y Parentesco'}
                                            </label>
                                            <input name={`emergencyName_${i}`} type="text" required defaultValue={participantsData[i]?.emergencyName || ''} placeholder={isEn ? 'Jane Doe (Mother)' : 'Ej: Maria Paz (Madre)'} className="w-full h-12 bg-black/50 border border-[#00d4ff]/30 px-4 text-xs text-[#00d4ff] placeholder:text-[#00d4ff]/30 outline-none focus:border-[#00d4ff] transition-colors" />
                                        </div>
                                        <div>
                                            <label className="text-[9px] text-[#00d4ff]/60 uppercase tracking-widest font-bold mb-2 block">
                                                {isEn ? 'Phone Number (Include +country code)' : 'Teléfono (Incluir +código de país)'}
                                            </label>
                                            <input name={`emergencyPhone_${i}`} type="tel" required defaultValue={participantsData[i]?.emergencyPhone || ''} placeholder={isEn ? '+1 123 456 7890' : 'Ej: +56 9 1234 5678'} className="w-full h-12 bg-black/50 border border-[#00d4ff]/30 px-4 text-xs text-[#00d4ff] placeholder:text-[#00d4ff]/30 outline-none focus:border-[#00d4ff] transition-colors" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    </div>

                    {/* RIGHT SIDEBAR: SUMMARY & PAYMENT */}
                    <div className="w-full lg:w-[400px] xl:w-[440px] shrink-0 sticky top-32 z-20">
                        <div className="bg-[#1a1512] border border-rock p-6 xl:p-8 shadow-2xl relative overflow-hidden flex flex-col gap-6">
                            <div className="absolute -right-20 -top-20 w-64 h-64 bg-[#00d4ff]/5 rounded-full blur-3xl mix-blend-screen"></div>
                            
                            <div className="space-y-4 relative z-10 w-full">
                                <h2 className="text-[#00d4ff] text-[9px] font-bold uppercase tracking-[0.3em]">{isEn ? 'Booking Summary' : 'Resumen de Itinerario'}</h2>
                                <div className="text-3xl font-title uppercase text-white tracking-tight leading-none">
                                    {exp ? (isEn ? exp.name_en : exp.name_es) : 'Expedición de Montaña'}
                                </div>
                                
                                <div className="flex flex-col gap-5 pt-5 border-t border-rock/50 mt-5 w-full">
                                    <div className="flex items-center gap-3 text-gray-300">
                                        <Calendar className="w-5 h-5 text-[#00d4ff] shrink-0" />
                                        <span className="text-xs uppercase tracking-widest font-bold capitalize">{displayDate}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3 text-gray-300">
                                        <div className="flex items-center gap-3">
                                            <Users className="w-5 h-5 text-[#00d4ff] shrink-0" />
                                            <span className="text-xs uppercase tracking-widest font-bold">{people} {isEn ? 'Explorers' : 'Exploradores'}</span>
                                        </div>
                                        {/* INCREMENT/DECREMENT CONTROLS */}
                                        <div className="flex border border-[#00d4ff]/30 bg-black rounded-sm overflow-hidden shrink-0">
                                            <button type="button" onClick={() => setPeople(Math.max(1, people - 1))} className="w-7 h-7 flex items-center justify-center hover:bg-[#00d4ff] hover:text-black transition-colors text-[#00d4ff] font-bold">-</button>
                                            <button type="button" onClick={() => setPeople(people + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-[#00d4ff] hover:text-black transition-colors text-[#00d4ff] border-l border-[#00d4ff]/30 font-bold">+</button>
                                        </div>
                                    </div>
                                    
                                    {airport && (
                                        <div className="flex items-center gap-3 text-gray-300">
                                            <Plane className="w-5 h-5 text-[#00d4ff] shrink-0" />
                                            <span className="text-[10px] md:text-xs uppercase tracking-widest font-bold">
                                                {isEn ? '+ Airport Transfer' : '+ Traslado Aeropuerto'} <span className="text-[#00d4ff]">(+$50)</span>
                                            </span>
                                        </div>
                                    )}

                                    {selectedServices && selectedServices.length > 0 && (
                                        <div className="pt-2 space-y-2">
                                            <span className="text-[9px] text-[#00d4ff] uppercase tracking-widest font-bold block mb-1">
                                                {isEn ? 'Selected Activities:' : 'Actividades Seleccionadas:'}
                                            </span>
                                            <div className="flex flex-wrap gap-1.5">
                                                {selectedServices.map((s, idx) => (
                                                    <span key={idx} className="bg-white/5 border border-white/10 px-2 py-1 text-[8px] uppercase tracking-tighter text-gray-400">
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="bg-black border border-[#00d4ff]/20 p-5 w-full text-center relative z-10 rounded-sm">
                                <div className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">
                                    {isEn ? 'Total Expedition Value' : 'Valor Total Expedición'}
                                </div>
                                <div className="text-xl font-title text-gray-300 tracking-tight mb-3">
                                    USD${totalPrice || '---'}
                                </div>
                                <div className="border-t border-[#00d4ff]/20 pt-3">
                                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/30 text-[9px] font-bold uppercase tracking-wider mb-1">
                                        {isBalancePayment 
                                            ? (isEn ? 'Final Balance (50%)' : 'Saldo Final a Pagar (50%)') 
                                            : (isEn ? 'Reservation Deposit Today (50%)' : 'Abono de Reserva Hoy (50%)')}
                                    </span>
                                    <div className="text-4xl xl:text-5xl font-title text-[#00d4ff] tracking-tighter">
                                        USD${chargeAmount || '---'}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
                                        {isBalancePayment
                                            ? (isEn ? 'Final payment confirming 100% of your booking.' : 'Pago final para confirmar tu expedición al 100%.')
                                            : (isEn ? `Remaining USD${balanceAmount} is requested 48h before departure.` : `El 50% restante (USD${balanceAmount}) se paga 48 hrs antes del tour.`)}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Section: Aceptación de políticas */}
                            <div className="space-y-4 pt-2 relative z-10 w-full text-left">
                                <label className="flex items-start space-x-3 text-xs cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        required
                                        checked={waiverAccepted}
                                        onChange={e => setWaiverAccepted(e.target.checked)}
                                        className="rounded accent-[#00d4ff] mt-0.5"
                                    />
                                    <span className="text-gray-300 text-[11px] leading-snug">
                                        {isEn ? (
                                            <>I accept the <strong>Waiver and Terms of Service</strong>.</>
                                        ) : (
                                            <>Acepto el <strong>Descargo de Responsabilidad (Waiver)</strong> y los Términos del Servicio.</>
                                        )}
                                    </span>
                                </label>

                                <label className="flex items-start space-x-3 text-xs cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        required
                                        checked={policyAccepted}
                                        onChange={e => setPolicyAccepted(e.target.checked)}
                                        className="rounded accent-[#00d4ff] mt-0.5"
                                    />
                                    <span className="text-gray-300 text-[11px] leading-snug">
                                        {isEn ? (
                                            <>I accept the <strong>Cancellation and Refund Policies</strong>.</>
                                        ) : (
                                            <>Acepto las <strong>Políticas de Cancelación y Reembolso</strong> de Fisura Expeditions.</>
                                        )}
                                    </span>
                                </label>
                            </div>

                            {/* PAYMENT ACTION */}
                            <div className="pt-2 relative z-10 w-full">
                                <button
                                    type="submit"
                                    disabled={loading || !chargeAmount || showPayPal}
                                    className="bg-[#00d4ff] text-black h-16 w-full font-title text-lg uppercase tracking-widest hover:bg-white disabled:opacity-50 transition-all shadow-[0_10px_30px_-10px_rgba(0,212,255,0.4)] relative"
                                >
                                    {showPayPal 
                                        ? (isEn ? 'Processing...' : 'Procesando...') 
                                        : (isBalancePayment 
                                            ? (isEn ? `Pay Final Balance (USD${chargeAmount}) →` : `Pagar Saldo Final (USD${chargeAmount}) →`)
                                            : (isEn ? `Pay 50% Deposit (USD${chargeAmount}) →` : `Pagar Abono 50% (USD${chargeAmount}) →`))
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </form>

                {/* PAYPAL / VIRTUALPOS MODAL OVERLAY */}
                {showPayPal && (
                    <div 
                        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowExitConfirm(true);
                            }
                        }}
                    >
                        <div className="min-h-screen px-4 py-10 flex flex-col items-center justify-center">
                            <div className="bg-[#f2f2f2] rounded-2xl w-full max-w-lg p-6 sm:p-8 relative shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden">
                                
                                {/* Exit Confirmation Dialog */}
                                {showExitConfirm && (
                                    <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in duration-200">
                                        <div className="bg-white rounded-2xl p-6 sm:p-7 max-w-sm w-full text-center shadow-2xl border border-black/10 animate-in zoom-in-95 duration-200">
                                            <h4 className="text-base font-title uppercase tracking-wider text-gray-900 mb-2">
                                                {isEn ? 'Exit payment process?' : '¿Deseas salir del proceso de pago?'}
                                            </h4>
                                            <p className="text-xs text-gray-600 mb-6 leading-relaxed">
                                                {isEn 
                                                    ? 'Your registration information is saved, but your booking will remain pending until payment is completed.' 
                                                    : 'Tus datos están guardados, pero tu reserva quedará pendiente hasta completar el pago.'
                                                }
                                            </p>
                                            <div className="flex flex-col gap-2.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowExitConfirm(false)}
                                                    className="w-full py-3 bg-[#00d4ff] hover:bg-[#00bfe6] text-black font-bold text-xs uppercase tracking-wider rounded-lg transition-colors cursor-pointer shadow-md"
                                                >
                                                    {isEn ? 'Continue with payment' : 'Continuar con el pago'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowExitConfirm(false);
                                                        setShowPayPal(false);
                                                    }}
                                                    className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs rounded-lg transition-colors cursor-pointer"
                                                >
                                                    {isEn ? 'Yes, exit' : 'Sí, salir'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Close Button */}
                                <button 
                                    type="button"
                                    onClick={() => setShowExitConfirm(true)}
                                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/5 hover:bg-black/10 transition-colors text-black font-bold z-10 text-xl cursor-pointer"
                                >
                                    ✕
                                </button>
                                
                                <div className="text-center text-black mb-5 mt-1">
                                    <h3 className="text-xl font-title uppercase tracking-widest mb-1 border-b border-black/10 pb-3">
                                        {isBalancePayment 
                                            ? (isEn ? 'Complete Final 50% Balance' : 'Completar Saldo Final (50%)') 
                                            : (isEn ? 'Confirm Booking with 50% Deposit' : 'Confirmar Reserva con Abono del 50%')}
                                    </h3>
                                    <div className="bg-black/5 rounded-xl p-3 my-3">
                                        <div className="text-gray-500 text-[10px] uppercase tracking-widest font-bold">
                                            {isEn ? 'Total Expedition Value:' : 'Valor Total Expedición:'} <span className="font-title text-gray-800 text-sm">USD${totalPrice}</span>
                                        </div>
                                        <div className="text-gray-700 text-[11px] font-bold uppercase tracking-wider mt-1">
                                            {isBalancePayment 
                                                ? (isEn ? 'Amount to pay today (50% Balance):' : 'Monto a pagar hoy (Saldo 50%):') 
                                                : (isEn ? 'Amount to pay today (50% Deposit):' : 'Monto a pagar hoy (Abono 50%):')}
                                        </div>
                                        <div className="text-[#00d4ff] text-4xl font-title tracking-tight mt-0.5">USD${chargeAmount}</div>
                                        {!isBalancePayment && (
                                            <p className="text-[10px] text-gray-500 mt-1">
                                                {isEn ? `Remaining USD${balanceAmount} is requested 48h before the tour.` : `El 50% restante (USD${balanceAmount}) te será solicitado 48 horas antes de la expedición.`}
                                            </p>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-2">
                                        {isEn 
                                            ? 'Select your preferred payment gateway below:' 
                                            : 'Selecciona la opción de pago que prefieras:'
                                        }
                                    </p>
                                </div>

                                {/* PAYMENT METHOD SELECTOR TABS */}
                                <div className="grid grid-cols-2 gap-2 p-1.5 bg-black/5 rounded-xl mb-5">
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('pix')}
                                        className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all text-center ${
                                            paymentMethod === 'pix'
                                                ? 'bg-white text-black shadow-md border-2 border-[#00d4ff]'
                                                : 'text-gray-500 hover:text-black hover:bg-white/50 border border-transparent'
                                        }`}
                                    >
                                        <div className="font-extrabold text-xs text-gray-900 leading-tight">
                                            Webpay · PIX · Bancos
                                        </div>
                                        <span className="text-[10px] text-gray-500 font-normal normal-case mt-1">
                                            Chile (Webpay/Bci) y Brasil (PIX)
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('paypal')}
                                        className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all text-center ${
                                            paymentMethod === 'paypal'
                                                ? 'bg-white text-black shadow-md border-2 border-[#003087]'
                                                : 'text-gray-500 hover:text-black hover:bg-white/50 border border-transparent'
                                        }`}
                                    >
                                        <div className="font-extrabold text-xs text-[#003087] leading-tight">
                                            PayPal / Tarjetas USD
                                        </div>
                                        <span className="text-[10px] text-gray-500 font-normal normal-case mt-1">
                                            {isEn ? 'Global Credit / Debit (USD)' : 'Tarjetas Internacionales (USD)'}
                                        </span>
                                    </button>
                                </div>

                                {virtualPosError && (
                                    <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 text-xs rounded-lg text-center font-medium">
                                        {virtualPosError}
                                    </div>
                                )}

                                {/* METHOD A: VIRTUALPOS (WEBPAY + MACH + KHIPU + PIX) */}
                                {paymentMethod === 'pix' && (
                                    <div className="space-y-4 px-1">
                                        <div className="bg-white p-5 rounded-xl border border-black/10 shadow-sm text-left">
                                            {/* Header with Title and Logos */}
                                            <div className="flex items-center justify-between mb-4 border-b border-black/5 pb-3">
                                                <div>
                                                    <div className="font-bold text-sm text-gray-900 leading-tight">
                                                        {isEn ? 'Chilean & Brazilian Payment Options' : 'Medios Locales: Chile y Brasil'}
                                                    </div>
                                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                                        {isEn ? 'Webpay, Bank Transfers, MACH & PIX' : 'Webpay Plus, Transferencia bancaria, MACH y PIX'}
                                                    </div>
                                                </div>
                                                <img 
                                                    src="/virtualpos-logotipo.png" 
                                                    alt="VirtualPOS" 
                                                    className="h-5 w-auto object-contain opacity-90"
                                                />
                                            </div>

                                            {/* Visual Badges of All Included Payment Methods */}
                                            <div className="mb-4">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">
                                                    {isEn ? 'Available on this gateway:' : 'Métodos que podrás elegir al continuar:'}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {/* Webpay */}
                                                    <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg border border-gray-200/70 hover:border-gray-300 transition-colors">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-[#E60028] flex-shrink-0"></span>
                                                        <div>
                                                            <div className="text-xs font-bold text-gray-900">Webpay Plus</div>
                                                            <div className="text-[10px] text-gray-500">Débito, Crédito, Prepago, CuentaRUT</div>
                                                        </div>
                                                    </div>

                                                    {/* MACH */}
                                                    <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg border border-gray-200/70 hover:border-gray-300 transition-colors">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-[#6A148E] flex-shrink-0"></span>
                                                        <div>
                                                            <div className="text-xs font-bold text-gray-900">MACH Bci</div>
                                                            <div className="text-[10px] text-gray-500">Pago móvil con app Bci</div>
                                                        </div>
                                                    </div>

                                                    {/* Khipu */}
                                                    <div className="flex items-center gap-2.5 p-2.5 bg-gray-50 rounded-lg border border-gray-200/70 hover:border-gray-300 transition-colors">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-[#0090DA] flex-shrink-0"></span>
                                                        <div>
                                                            <div className="text-xs font-bold text-gray-900">Khipu</div>
                                                            <div className="text-[10px] text-gray-500">Transferencia bancaria directa</div>
                                                        </div>
                                                    </div>

                                                    {/* PIX Brasil */}
                                                    <div className="flex items-center gap-2.5 p-2.5 bg-[#f0fdf9] rounded-lg border border-[#32bcad]/30 hover:border-[#32bcad] transition-colors">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-[#32bcad] flex-shrink-0"></span>
                                                        <div>
                                                            <div className="text-xs font-bold text-gray-900">PIX (Brasil)</div>
                                                            <div className="text-[10px] text-[#248277]">QR / Copia e Cola en Reales</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-[11px] text-gray-600 leading-relaxed mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                                                {isEn 
                                                    ? 'Click below to enter the secure checkout where you can choose Webpay (Chilean cards/CuentaRUT), MACH, Khipu transfer, or PIX (converted to BRL for Brazilian banks).'
                                                    : 'Al hacer clic entrarás a la pasarela segura donde podrás pagar directamente con Webpay (Crédito, Débito, CuentaRUT), MACH, Transferencia Khipu o PIX (con conversión oficial a Reales).'
                                                }
                                            </p>

                                            <button
                                                type="button"
                                                disabled={virtualPosLoading}
                                                onClick={handleStartVirtualPosPayment}
                                                className="w-full py-4 bg-[#00d4ff] hover:bg-[#00bfe6] text-black font-bold text-sm uppercase tracking-wider rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                                            >
                                                {virtualPosLoading ? (
                                                    <span>{isEn ? 'Connecting with Gateway...' : 'Conectando con Pasarela...'}</span>
                                                ) : (
                                                    <>
                                                        <span>{isEn ? 'Select Payment Method & Pay →' : 'Elegir Medio de Pago y Pagar →'}</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* METHOD B: PAYPAL BUTTONS */}
                                {paymentMethod === 'paypal' && (
                                <div className="px-1 relative z-0 w-full">
                                    <PayPalButtons 
                                        style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                                        createOrder={(data, actions) => {
                                            if (!actions.order) throw new Error("Could not construct PayPal Order");
                                            const orderDesc = isBalancePayment
                                                ? `${exp ? (isEn ? exp.name_en : exp.name_es) : 'Expedición'} - Saldo Final (50%)`
                                                : `${exp ? (isEn ? exp.name_en : exp.name_es) : 'Expedición'} - Abono Reserva (50%)`;
                                            return actions.order.create({
                                                intent: "CAPTURE",
                                                purchase_units: [
                                                    {
                                                        description: orderDesc,
                                                        amount: {
                                                            currency_code: 'USD',
                                                            value: chargeAmount.toString() || '0.00'
                                                        }
                                                    }
                                                ]
                                            });
                                        }}
                                        onApprove={(data, actions) => {
                                            if (actions.order) {
                                                setLoading(true);
                                                return actions.order.capture().then(async (details) => {
                                                    console.log("Payment approved: ", details);
                                                    
                                                    const leadEmail = leadEmailFromState;
                                                    
                                                    // Inject email and selected_services into lead participant to persist in Supabase JSONB
                                                    const updatedParticipants = [...participantsData];
                                                    if (updatedParticipants.length > 0) {
                                                        updatedParticipants[0] = {
                                                            ...updatedParticipants[0],
                                                            email: leadEmailFromState
                                                        };
                                                        if (selectedServices) {
                                                            updatedParticipants[0].selected_services = selectedServices;
                                                        }
                                                    }

                                                    const nextStatus = isBalancePayment ? 'paid' : 'partially_paid';
                                                    const notes = isBalancePayment
                                                        ? `100% Pagado. Saldo final de USD${chargeAmount} recibido vía PayPal (${details.id}).`
                                                        : `Abono 50% de USD${chargeAmount} recibido vía PayPal (${details.id}). Saldo pendiente: USD${balanceAmount}.`;

                                                    // Update database
                                                    try {
                                                        if (bookingId) {
                                                            const { error } = await supabase
                                                                .from('bookings')
                                                                .update({
                                                                    payment_status: nextStatus,
                                                                    paypal_order_id: details.id,
                                                                    participants: updatedParticipants,
                                                                    waiver_accepted: true,
                                                                    cancellation_policy_accepted: true,
                                                                    internal_notes: notes
                                                                })
                                                                .eq('id', bookingId);
 
                                                            if (error) {
                                                                console.error("Supabase update error:", error);
                                                            } else {
                                                                console.log(`Booking successfully updated to ${nextStatus} in Supabase!`);
                                                                // Trigger booking-status-email Edge Function
                                                                await triggerStatusEmail(nextStatus, bookingId, details.id, updatedParticipants);
                                                            }
                                                        } else {
                                                            // Fallback: If for some reason bookingId was not set, insert it now
                                                            const bookingData = {
                                                                expedition_slug: expId || 'unknown',
                                                                start_date: date,
                                                                people_count: people,
                                                                total_price: totalPrice,
                                                                airport_transfer: airport,
                                                                payment_status: nextStatus,
                                                                paypal_order_id: details.id,
                                                                participants: updatedParticipants,
                                                                waiver_accepted: true,
                                                                cancellation_policy_accepted: true,
                                                                internal_notes: notes
                                                            };
                                                            const { data: fallbackData, error } = await supabase
                                                                .from('bookings')
                                                                .insert([bookingData])
                                                                .select('id')
                                                                .single();
                                                            if (error) {
                                                                console.error("Supabase insert fallback error:", error);
                                                            } else if (fallbackData) {
                                                                // Trigger booking-status-email Edge Function
                                                                await triggerStatusEmail(nextStatus, fallbackData.id, details.id, updatedParticipants);
                                                            }
                                                        }
                                                    } catch (err) { 
                                                        console.error("Database status update exception", err);
                                                    }

                                                    navigate(getLocalizedPath(`/booking-success?booking_id=${bookingId || ''}&type=${isBalancePayment ? 'balance' : 'deposit'}`, lang));
                                                });
                                            }
                                            return Promise.resolve();
                                        }}
                                        onError={(err) => {
                                            console.error("PayPal Checkout onError", err);
                                        }}
                                        onCancel={() => {
                                            console.log("PayPal Checkout onCancel");
                                        }}
                                    />
                                </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Registration;
